import { useState, useEffect } from 'react';
import axios from 'axios';
import { Inbox, Check, X, Edit2, Trash2, Import, LogIn, LogOut, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getItalianDateTime = () => {
  const now = new Date();
  const italianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = italianTime.getFullYear();
  const month = String(italianTime.getMonth() + 1).padStart(2, '0');
  const day = String(italianTime.getDate()).padStart(2, '0');
  const hours = String(italianTime.getHours()).padStart(2, '0');
  const minutes = String(italianTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const DaImportare = () => {
  const [richieste, setRichieste] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [idFornitori, setIdFornitori] = useState([]);
  const [bins, setBins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [filterTipo, setFilterTipo] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [richiesteRes, fornitoriRes, prodottiRes, idFornitoriRes, binsRes] = await Promise.all([
        axios.get(`${API}/richieste-importazione`),
        axios.get(`${API}/fornitori`),
        axios.get(`${API}/prodotti`),
        axios.get(`${API}/id-fornitori`),
        axios.get(`${API}/bins`)
      ]);
      setRichieste(richiesteRes.data);
      setFornitori(fornitoriRes.data);
      setProdotti(prodottiRes.data);
      setIdFornitori(idFornitoriRes.data);
      setBins(binsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const startEditIngresso = (richiesta) => {
    setEditingId(richiesta.id);
    // Usa la data/ora impostata dall'operatore, oppure fallback alla data corrente
    let dataInizio = getItalianDateTime();
    if (richiesta.data_inizio) {
      // Converte la data dalla richiesta in formato datetime-local
      const d = new Date(richiesta.data_inizio);
      if (!isNaN(d.getTime())) {
        const italianTime = new Date(d.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
        const year = italianTime.getFullYear();
        const month = String(italianTime.getMonth() + 1).padStart(2, '0');
        const day = String(italianTime.getDate()).padStart(2, '0');
        const hours = String(italianTime.getHours()).padStart(2, '0');
        const minutes = String(italianTime.getMinutes()).padStart(2, '0');
        dataInizio = `${year}-${month}-${day}T${hours}:${minutes}`;
      }
    }
    setEditForm({
      tipo: 'ingresso',
      bin_number: richiesta.bin_number,
      doccia_number: richiesta.doccia_number,
      kg: richiesta.kg,
      fornitore: richiesta.fornitore_nome || '',
      id_fornitore: richiesta.id_fornitore || '',
      prodotto: richiesta.prodotto_nome || '',
      lotto_number: richiesta.lotto_number || '',
      data_inizio: dataInizio  // Data dall'operatore
    });
  };

  const startEditUscita = (richiesta) => {
    setEditingId(richiesta.id);
    let dataOraUscita = getItalianDateTime();
    if (richiesta.data_ora_uscita) {
      const d = new Date(richiesta.data_ora_uscita);
      const italianTime = new Date(d.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
      const year = italianTime.getFullYear();
      const month = String(italianTime.getMonth() + 1).padStart(2, '0');
      const day = String(italianTime.getDate()).padStart(2, '0');
      const hours = String(italianTime.getHours()).padStart(2, '0');
      const minutes = String(italianTime.getMinutes()).padStart(2, '0');
      dataOraUscita = `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    setEditForm({
      tipo: 'uscita',
      tipo_operazione: richiesta.tipo_operazione,
      bin_number: richiesta.bin_number,
      doccia_number: richiesta.doccia_number,
      kg_uscita: richiesta.kg_uscita,
      nuovo_bin_number: richiesta.nuovo_bin_number || '',
      nuova_doccia_number: richiesta.nuova_doccia_number || '',
      data_ora_uscita: dataOraUscita
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleImportIngresso = async (id) => {
    if (!editForm.fornitore || !editForm.lotto_number || !editForm.prodotto) {
      toast.error('Fornitore, Prodotto e Lotto sono obbligatori per importare');
      return;
    }
    
    if (!editForm.id_fornitore) {
      toast.error('ID è obbligatorio per importare');
      return;
    }

    try {
      await axios.post(`${API}/richieste-importazione/${id}/importa`, {
        bin_number: editForm.bin_number,
        doccia_number: parseInt(editForm.doccia_number),
        kg: parseFloat(editForm.kg),
        fornitore: editForm.fornitore,
        id_fornitore: editForm.id_fornitore,
        prodotto: editForm.prodotto,
        lotto_number: editForm.lotto_number,
        data_inizio: new Date(editForm.data_inizio).toISOString()
      });
      
      toast.success('Bin importato con successo!');
      setEditingId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante importazione');
    }
  };

  const handleImportUscita = async (id) => {
    try {
      await axios.post(`${API}/richieste-importazione/${id}/importa-uscita`, {
        tipo_operazione: editForm.tipo_operazione,
        bin_number: editForm.bin_number,
        doccia_number: parseInt(editForm.doccia_number),
        kg_uscita: parseFloat(editForm.kg_uscita),
        nuovo_bin_number: editForm.nuovo_bin_number || null,
        nuova_doccia_number: editForm.nuova_doccia_number ? parseInt(editForm.nuova_doccia_number) : null,
        data_ora_uscita: new Date(editForm.data_ora_uscita).toISOString()
      });
      
      toast.success('Uscita registrata con successo!');
      setEditingId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante importazione uscita');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa richiesta?')) return;
    
    try {
      await axios.delete(`${API}/richieste-importazione/${id}`);
      toast.success('Richiesta eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  const richiesteIngresso = richieste.filter(r => r.tipo === 'ingresso' || !r.tipo);
  const richiesteUscita = richieste.filter(r => r.tipo === 'uscita');

  const filteredRichieste = filterTipo === 'all' 
    ? richieste 
    : filterTipo === 'ingresso' 
      ? richiesteIngresso 
      : richiesteUscita;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
        <div className="w-8 h-8 border-4 border-[#FFE500] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const getTipoOperazioneLabel = (tipo) => {
    switch(tipo) {
      case 'uscita_totale': return 'Uscita Totale';
      case 'uscita_parziale': return 'Uscita Parziale';
      case 'spostamento': return 'Spostamento';
      default: return tipo;
    }
  };

  return (
    <div className="space-y-6" data-testid="da-importare-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#FFE500] p-2 rounded-xl">
            <Inbox className="w-5 h-5 text-[#0A1628]" />
          </div>
          <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Da Importare</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilterTipo('all')}
            className={`px-3 py-2 text-xs font-bold uppercase rounded-xl transition-colors ${
              filterTipo === 'all' 
                ? 'bg-[#FFE500] text-[#0A1628]' 
                : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
            }`}
          >
            Tutti ({richieste.length})
          </button>
          <button
            onClick={() => setFilterTipo('ingresso')}
            className={`px-3 py-2 text-xs font-bold uppercase rounded-xl flex items-center gap-1 transition-colors ${
              filterTipo === 'ingresso' 
                ? 'bg-emerald-500 text-white' 
                : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30'
            }`}
          >
            <LogIn className="w-3 h-3" /> IN ({richiesteIngresso.length})
          </button>
          <button
            onClick={() => setFilterTipo('uscita')}
            className={`px-3 py-2 text-xs font-bold uppercase rounded-xl flex items-center gap-1 transition-colors ${
              filterTipo === 'uscita' 
                ? 'bg-red-500 text-white' 
                : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
            }`}
          >
            <LogOut className="w-3 h-3" /> OUT ({richiesteUscita.length})
          </button>
        </div>
      </div>

      {filteredRichieste.length === 0 ? (
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-12 text-center" data-testid="empty-state">
          <Inbox className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-white">Nessuna richiesta</h3>
          <p className="text-sm text-white/50 mt-2">Le richieste degli operatori appariranno qui</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRichieste.map((richiesta) => {
            const isIngresso = richiesta.tipo === 'ingresso' || !richiesta.tipo;
            const isUscita = richiesta.tipo === 'uscita';

            return (
              <div 
                key={richiesta.id} 
                className={`bg-white/10 backdrop-blur border rounded-2xl p-5 ${
                  isIngresso ? 'border-l-4 border-l-emerald-500 border-white/20' : 'border-l-4 border-l-red-500 border-white/20'
                }`}
                data-testid={`richiesta-${richiesta.id}`}
              >
                {editingId === richiesta.id ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-3 border-b border-white/10">
                      <h4 className="font-bold text-white flex items-center gap-2">
                        {isIngresso ? (
                          <><LogIn className="w-4 h-4 text-emerald-400" /> Completa Ingresso</>
                        ) : (
                          <><LogOut className="w-4 h-4 text-red-400" /> Conferma Uscita</>
                        )}
                      </h4>
                      <button onClick={cancelEdit} className="text-white/50 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    {isIngresso ? (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">N. Bin</label>
                            <input
                              type="text"
                              value={editForm.bin_number}
                              onChange={(e) => setEditForm({...editForm, bin_number: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Doccia</label>
                            <select
                              value={editForm.doccia_number}
                              onChange={(e) => setEditForm({...editForm, doccia_number: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                            >
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                                <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">KG</label>
                            <input
                              type="number"
                              step="0.01"
                              value={editForm.kg}
                              onChange={(e) => setEditForm({...editForm, kg: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Fornitore *</label>
                            <select
                              value={editForm.fornitore}
                              onChange={(e) => setEditForm({...editForm, fornitore: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                              required
                            >
                              <option value="" className="bg-[#0A1628]">Seleziona...</option>
                              {fornitori.map(f => (
                                <option key={f.nome} value={f.nome} className="bg-[#0A1628]">{f.nome}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">ID *</label>
                            <select
                              value={editForm.id_fornitore}
                              onChange={(e) => setEditForm({...editForm, id_fornitore: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-cyan-500/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                              required
                            >
                              <option value="" className="bg-[#0A1628]">Seleziona...</option>
                              {idFornitori.map(idf => (
                                <option key={idf.codice} value={idf.codice} className="bg-[#0A1628]">
                                  {idf.codice}{idf.descrizione ? ` - ${idf.descrizione}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Prodotto *</label>
                            <select
                              value={editForm.prodotto}
                              onChange={(e) => setEditForm({...editForm, prodotto: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                              required
                            >
                              <option value="" className="bg-[#0A1628]">Seleziona...</option>
                              {prodotti.map(p => (
                                <option key={p.nome} value={p.nome} className="bg-[#0A1628]">{p.nome}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Lotto *</label>
                            <input
                              type="text"
                              value={editForm.lotto_number}
                              onChange={(e) => setEditForm({...editForm, lotto_number: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                              placeholder="es. 3096"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Data Ingresso</label>
                            <input
                              type="datetime-local"
                              value={editForm.data_inizio}
                              onChange={(e) => setEditForm({...editForm, data_inizio: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                            />
                          </div>
                        </div>

                        <div className="flex gap-2 pt-3 border-t border-white/10">
                          <button
                            onClick={() => handleImportIngresso(richiesta.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-emerald-600 transition-colors"
                          >
                            <Import className="w-4 h-4" />
                            Importa in Bins
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="px-4 py-2 bg-white/10 text-white border border-white/20 rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-white/20 transition-colors"
                          >
                            Annulla
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Tipo</label>
                            <select
                              value={editForm.tipo_operazione}
                              onChange={(e) => setEditForm({...editForm, tipo_operazione: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                            >
                              <option value="uscita_totale" className="bg-[#0A1628]">Uscita Totale</option>
                              <option value="uscita_parziale" className="bg-[#0A1628]">Uscita Parziale</option>
                              <option value="spostamento" className="bg-[#0A1628]">Spostamento</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Bin</label>
                            <input
                              type="text"
                              value={editForm.bin_number}
                              onChange={(e) => setEditForm({...editForm, bin_number: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Doccia</label>
                            <select
                              value={editForm.doccia_number}
                              onChange={(e) => setEditForm({...editForm, doccia_number: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                            >
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                                <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">KG Uscita</label>
                            <input
                              type="number"
                              step="0.01"
                              value={editForm.kg_uscita}
                              onChange={(e) => setEditForm({...editForm, kg_uscita: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                            />
                          </div>
                          {editForm.tipo_operazione === 'spostamento' && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Nuovo Bin</label>
                                <input
                                  type="text"
                                  value={editForm.nuovo_bin_number}
                                  onChange={(e) => setEditForm({...editForm, nuovo_bin_number: e.target.value})}
                                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Nuova Doccia</label>
                                <select
                                  value={editForm.nuova_doccia_number}
                                  onChange={(e) => setEditForm({...editForm, nuova_doccia_number: e.target.value})}
                                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                                >
                                  <option value="" className="bg-[#0A1628]">Seleziona...</option>
                                  {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                                    <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
                                  ))}
                                </select>
                              </div>
                            </>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Data/Ora Uscita</label>
                            <input
                              type="datetime-local"
                              value={editForm.data_ora_uscita}
                              onChange={(e) => setEditForm({...editForm, data_ora_uscita: e.target.value})}
                              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                            />
                          </div>
                        </div>

                        <div className="flex gap-2 pt-3 border-t border-white/10">
                          <button
                            onClick={() => handleImportUscita(richiesta.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-red-600 transition-colors"
                          >
                            <Check className="w-4 h-4" />
                            Conferma Uscita
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="px-4 py-2 bg-white/10 text-white border border-white/20 rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-white/20 transition-colors"
                          >
                            Annulla
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-4 lg:gap-6">
                      <div className="flex items-center gap-2">
                        {isIngresso ? (
                          <LogIn className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <LogOut className="w-5 h-5 text-red-400" />
                        )}
                        <span className={`text-xs font-bold uppercase ${isIngresso ? 'text-emerald-400' : 'text-red-400'}`}>
                          {isIngresso ? 'INGRESSO' : getTipoOperazioneLabel(richiesta.tipo_operazione)}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Bin</p>
                        <p className="font-bold text-white text-lg">{richiesta.bin_number}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Doccia</p>
                        <p className="font-bold text-white">{richiesta.doccia_number}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">KG</p>
                        <p className={`font-bold ${isIngresso ? 'text-emerald-400' : 'text-red-400'}`}>
                          {isIngresso ? richiesta.kg : richiesta.kg_uscita} kg
                        </p>
                      </div>
                      {isUscita && richiesta.tipo_operazione === 'spostamento' && (
                        <div className="flex items-center gap-2">
                          <ArrowRight className="w-4 h-4 text-white/40" />
                          <div>
                            <p className="text-xs text-white/50">Verso</p>
                            <p className="font-medium text-white">
                              Bin {richiesta.nuovo_bin_number} / D{richiesta.nuova_doccia_number}
                            </p>
                          </div>
                        </div>
                      )}
                      {richiesta.fornitore_nome && (
                        <div>
                          <p className="text-xs text-white/50">Fornitore indicato</p>
                          <p className="text-sm text-white">{richiesta.fornitore_nome}</p>
                        </div>
                      )}
                      {richiesta.id_fornitore && (
                        <div>
                          <p className="text-xs text-white/50">ID</p>
                          <p className="text-sm font-bold text-cyan-400">{richiesta.id_fornitore}</p>
                        </div>
                      )}
                      {richiesta.prodotto_nome && (
                        <div>
                          <p className="text-xs text-white/50">Prodotto</p>
                          <p className="text-sm text-white">{richiesta.prodotto_nome}</p>
                        </div>
                      )}
                      {richiesta.lotto_number && (
                        <div>
                          <p className="text-xs text-white/50">Lotto</p>
                          <p className="text-sm text-white">{richiesta.lotto_number}</p>
                        </div>
                      )}
                      {richiesta.note && (
                        <div>
                          <p className="text-xs text-white/50">Note</p>
                          <p className="text-sm text-white">{richiesta.note}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-white/50">Ricevuto</p>
                        <p className="text-xs text-white/70">{format(new Date(richiesta.created_at), 'dd/MM/yy HH:mm')}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => isIngresso ? startEditIngresso(richiesta) : startEditUscita(richiesta)}
                        className={`flex items-center gap-2 px-4 py-2 text-white rounded-xl font-bold uppercase tracking-wide text-xs transition-colors ${
                          isIngresso ? 'bg-blue-500 hover:bg-blue-600' : 'bg-red-500 hover:bg-red-600'
                        }`}
                        data-testid={`edit-richiesta-${richiesta.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                        {isIngresso ? 'Completa e Importa' : 'Verifica e Conferma'}
                      </button>
                      <button
                        onClick={() => handleDelete(richiesta.id)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-xl transition-colors"
                        data-testid={`delete-richiesta-${richiesta.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DaImportare;
